import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Building2, CalendarDays, CheckCircle2, Clock3, Mail, MessageCircleMore, Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { footerDetails } from "@/components/equinox/data";
import { getLeadRequestPayload, trackAdClickEvent, trackLeadEvent, trackLeadSubmitSuccess } from "@/lib/leadTracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactCards = [
  { label: "Email", value: footerDetails.email, icon: Mail },
  { label: "WhatsApp", value: footerDetails.whatsapp, icon: MessageCircleMore },
  { label: "Phone", value: footerDetails.phone, icon: Phone },
];

const bookingSignals = [
  { label: "Typical response", value: "Within 24 hours", icon: Clock3, testId: "contact-booking-signal-response" },
  { label: "Call format", value: "15-min founder-led audit", icon: CalendarDays, testId: "contact-booking-signal-format" },
  { label: "Trust layer", value: "Direct Equinox follow-up", icon: ShieldCheck, testId: "contact-booking-signal-trust" },
];

const bookingCadence = ["Mon", "Tue", "Wed", "Thu", "Sat"];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", website: "", goal: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submittedLead, setSubmittedLead] = useState(null);

  const buildWhatsappFollowUpHref = (lead) => {
    const message = [
      `Hi Equinox, I just submitted a growth call request${lead?.company ? ` for ${lead.company}` : ""}.`,
      lead?.name ? `Name: ${lead.name}` : null,
      lead?.email ? `Email: ${lead.email}` : null,
      lead?.website ? `Website/App: ${lead.website}` : null,
      lead?.goal ? `Goal: ${lead.goal}` : null,
      "I’d like to confirm the next review window.",
    ]
      .filter(Boolean)
      .join("\n");

    return `${footerDetails.whatsappHref}?text=${encodeURIComponent(message)}`;
  };

  const whatsappFollowUpHref = buildWhatsappFollowUpHref(submittedLead);

  const submitForm = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      const leadPayload = getLeadRequestPayload(form, { source: "contact_page", whatsapp_handoff_opened: true });
      const response = await axios.post(`${API}/call-requests`, leadPayload);
      const leadSnapshot = { ...form };
      toast.success("Your growth call request has been received.");
      setSubmittedLead(leadSnapshot);
      setForm({ name: "", email: "", company: "", website: "", goal: "" });
      trackLeadSubmitSuccess({ leadId: response.data.id, source: "contact_page" });
      trackAdClickEvent({ label: "contact_form_submit", metadata: { placement: "contact_form", lead_id: response.data.id } });
      window.open(buildWhatsappFollowUpHref(leadSnapshot), "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to submit right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="px-6 pb-20 pt-16 sm:px-8 lg:px-12 lg:pb-28 lg:pt-20">
      <div className="mx-auto max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <p className="section-kicker">Contact / Book Call</p>
          <h1 className="section-heading mt-5 text-white">Book a Free Growth Call</h1>
          <p className="section-copy mt-6 text-white/[0.64]">
            Book a free growth call to diagnose acquisition inefficiencies, CAC pressure, and where Equinox can unlock stronger scale.
          </p>
          <p className="mt-4 text-sm uppercase tracking-[0.24em] text-blue-100">Free audit • No commitment • 15-min call</p>
        </motion.div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <form onSubmit={submitForm} className="glitter-card glass-panel rounded-[2rem] p-6 lg:p-7" data-testid="contact-form">
            {submittedLead && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="success-panel-shell mb-6 rounded-[1.7rem] border border-emerald-300/18 bg-emerald-400/[0.07] p-5"
                data-testid="contact-success-panel"
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <div className="max-w-xl">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-emerald-300/20 bg-emerald-400/12 text-emerald-200">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                    <p className="mt-4 text-[11px] uppercase tracking-[0.28em] text-emerald-100/80" data-testid="contact-success-panel-kicker">
                      Request received
                    </p>
                    <h2 className="mt-3 font-display text-2xl tracking-[-0.04em] text-white" data-testid="contact-success-panel-heading">
                      Thanks{submittedLead.name ? `, ${submittedLead.name}` : ""} — your growth call request is in.
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-white/[0.72]" data-testid="contact-success-panel-copy">
                      We’ll review the details and reach out with the next suitable window. A prefilled WhatsApp follow-up has also been prepared so reaching out takes one tap.
                    </p>
                  </div>

                  <a
                    href={whatsappFollowUpHref}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackAdClickEvent({ label: "contact_success_whatsapp", eventName: "whatsapp_click", metadata: { placement: "success_panel" } })}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200/24 bg-emerald-400/12 px-6 py-4 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-200/38 hover:bg-emerald-400/18"
                    data-testid="contact-success-whatsapp-button"
                  >
                    Confirm on WhatsApp
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-3">
                  {[
                    { label: "Response window", value: "Usually within 24 hours" },
                    { label: "Next step", value: "Founder-led call confirmation" },
                    { label: "Best follow-up", value: "WhatsApp for fastest handoff" },
                  ].map((item) => (
                    <div key={item.label} className="rounded-[1.2rem] border border-white/[0.08] bg-black/20 p-4" data-testid={`contact-success-detail-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">{item.label}</p>
                      <p className="mt-2 text-sm leading-6 text-white/[0.8]">{item.value}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block text-sm text-white/[0.75]">
                <span>Name</span>
                <input
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                  data-testid="contact-form-name-input"
                />
              </label>
              <label className="block text-sm text-white/[0.75]">
                <span>Email</span>
                <input
                  value={form.email}
                  onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                  className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                  data-testid="contact-form-email-input"
                />
              </label>
              <label className="block text-sm text-white/[0.75]">
                <span>App / Company</span>
                <input
                  value={form.company}
                  onChange={(event) => setForm((current) => ({ ...current, company: event.target.value }))}
                  className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                  data-testid="contact-form-company-input"
                />
              </label>
              <label className="block text-sm text-white/[0.75]">
                <span>Website / App link</span>
                <input
                  value={form.website}
                  onChange={(event) => setForm((current) => ({ ...current, website: event.target.value }))}
                  className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                  data-testid="contact-form-website-input"
                />
              </label>
            </div>

            <label className="mt-4 block text-sm text-white/[0.75]">
              <span>What growth outcome do you want most?</span>
              <textarea
                value={form.goal}
                onChange={(event) => setForm((current) => ({ ...current, goal: event.target.value }))}
                rows={5}
                className="mt-3 w-full rounded-[1.4rem] border border-white/[0.08] bg-black/20 px-4 py-4 text-white outline-none transition-colors focus:border-blue-300/40"
                data-testid="contact-form-goal-input"
              />
            </label>

            <div className="booking-strip-shell premium-border mt-6 rounded-[1.7rem] border border-white/[0.08] bg-black/25 p-5" data-testid="contact-booking-strip">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-xl">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-blue-100/90" data-testid="contact-booking-strip-kicker">
                    Priority booking window
                  </p>
                  <h2 className="mt-3 font-display text-2xl tracking-[-0.04em] text-white" data-testid="contact-booking-strip-heading">
                    Fast, founder-led scheduling with clear next-step timing.
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-white/[0.66]" data-testid="contact-booking-strip-copy">
                    Submit the form or message directly on WhatsApp — Equinox usually confirms the next suitable review window within one business day.
                  </p>
                </div>

                <div className="booking-cadence inline-flex flex-wrap gap-2" data-testid="contact-booking-cadence">
                  {bookingCadence.map((day, index) => (
                    <span
                      key={day}
                      className={`rounded-full px-3 py-2 text-xs uppercase tracking-[0.22em] ${index === 1 || index === 3 ? "border border-blue-300/35 bg-blue-400/12 text-blue-100" : "border border-white/[0.08] bg-white/[0.04] text-white/[0.54]"}`}
                      data-testid={`contact-booking-cadence-${day.toLowerCase()}`}
                    >
                      {day}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-3">
                {bookingSignals.map(({ label, value, icon: Icon, testId }) => (
                  <div key={label} className="rounded-[1.2rem] border border-white/[0.08] bg-white/[0.04] p-4" data-testid={testId}>
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/[0.08] bg-black/30 text-blue-100">
                      <Icon className="h-4 w-4" />
                    </div>
                    <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">{label}</p>
                    <p className="mt-2 text-sm leading-6 text-white/[0.78]">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <button
                type="submit"
                disabled={submitting}
                onClick={() => trackLeadEvent("cta_click", { label: "contact_form_submit_button", metadata: { placement: "contact_form" } })}
                className="glass-button glass-panel rounded-full px-7 py-4 text-sm font-medium text-white transition-all duration-300 hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="contact-form-submit-button"
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  {submitting ? "Submitting..." : "Get Your Growth Plan"}
                </span>
              </button>

              <a
                href={footerDetails.whatsappHref}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackAdClickEvent({ label: "contact_priority_review", eventName: "whatsapp_click", metadata: { placement: "contact_form_secondary" } })}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-7 py-4 text-sm font-medium text-white/82 backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-300/30 hover:bg-blue-400/10 hover:text-white"
                data-testid="contact-booking-secondary-button"
              >
                Book Priority Review
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            <p className="mt-4 text-xs uppercase tracking-[0.22em] text-white/[0.48]" data-testid="contact-booking-trust-note">
              No generic sales deck • direct response from Equinox
            </p>
          </form>

          <div>
        <div className="grid gap-6 md:grid-cols-3">
          {contactCards.map(({ label, value, icon: Icon }, index) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.55, delay: index * 0.06 }}
              className="glitter-card glass-panel cinematic-card rounded-[1.8rem] p-5"
              data-testid={`contact-card-${label.toLowerCase()}`}
            >
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.12] bg-gradient-to-br from-white/10 to-blue-500/16 text-white">
                <Icon className="h-5 w-5" />
              </div>
              <p className="text-xs uppercase tracking-[0.22em] text-white/[0.42]">{label}</p>
              <p className="mt-3 text-sm leading-7 text-white/[0.72]">{value}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 glitter-card glass-panel rounded-[2rem] p-6 lg:p-7">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.06] text-blue-100">
            <Building2 className="h-5 w-5" />
          </div>
          <h2 className="font-display text-3xl text-white">Direct access. Clean process. Serious growth conversation.</h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-white/[0.64]">
            This page is designed for one thing: turning interested operators into qualified conversations with minimal friction.
          </p>
        </div>
          </div>
        </div>
      </div>
    </section>
  );
}